import type { Carrier } from "@/app/redux/slices/geocodeFiltersSlice";
import carriersData from "./carriers.json";

/**
 * Carrier (sealine) catalog.
 *
 * The list is bundled from `carriers.json` (harvested once from the carriers
 * feed): there is NO carrier API call at runtime. To refresh the catalog, replace
 * `carriers.json` with a newer dump; the shape is `{ data: [{ id, scac, name,
 * image_url }] }`.
 *
 * `scac_codes[0]` is the canonical SCAC used as the `scac` query param when
 * searching schedules.
 */
interface RawCarrier {
	id: string | number;
	scac: string | null;
	name: string;
	image_url?: string | null;
}

export const CARRIERS: Carrier[] = (carriersData.data as RawCarrier[])
	.filter((carrier) => Boolean(carrier.scac))
	.map((carrier) => ({
		id: carrier.id,
		name: carrier.name,
		scac_codes: [carrier.scac as string],
		image_url: carrier.image_url ?? null,
	}));

/** Flat list of canonical SCAC codes (scac_codes[0]) for every carrier. */
export const ALL_CARRIER_SCACS: string[] = CARRIERS.map(
	(carrier) => carrier.scac_codes[0],
).filter((scac): scac is string => Boolean(scac));

/** SCAC → carrier, for looking a sailing's carrier up by its code. */
const BY_SCAC = new Map<string, Carrier>(
	CARRIERS.flatMap((carrier) =>
		carrier.scac_codes.map((scac) => [scac.toUpperCase(), carrier] as const),
	),
);

/**
 * The carrier's logo, or `null` when the catalog does not have one.
 *
 * The catalog is bundled, not fetched, so a carrier the provider returns may
 * simply not be in it: callers fall back to a generic mark rather than a
 * broken image.
 */
export const carrierLogo = (scac: string | null | undefined): string | null =>
	scac ? (BY_SCAC.get(scac.toUpperCase())?.image_url ?? null) : null;
